import { useState, useEffect, useCallback } from 'react'
import { Document } from 'flexsearch'

export function useSearch(gists) {
  const [searchIndex, setSearchIndex] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [gistMap, setGistMap] = useState(new Map())

  useEffect(() => {
    if (!gists || gists.length === 0) return

    console.log(`[FlexSearch] Building index for ${gists.length} gists...`)

    const startTime = performance.now()

    const index = new Document({
      document: {
        id: "gist_id",
        index: ["description", "filenames", "languages", "content"],
        store: ["gist_id", "description"]
      },
      tokenize: "forward",
      resolution: 9,
      optimize: true,
      context: {
        depth: 2,
        resolution: 5
      }
    })

    const documents = gists.map((gist) => ({
      gist_id: gist.gist_id,
      description: gist.description || '',
      filenames: gist.files.map((f) => f.filename).join(' '),
      languages: gist.files.map((f) => f.language || '').join(' '),
      content: gist.files.map((f) => f.content || '').join(' '),
    }))

    let totalChars = 0
    documents.forEach(doc => {
      totalChars += doc.description.length + doc.filenames.length + doc.languages.length + doc.content.length
    })

    const map = new Map()
    documents.forEach(doc => {
      index.add(doc)
      map.set(doc.gist_id, doc)
    })

    const endTime = performance.now()
    const buildTime = (endTime - startTime).toFixed(2)
    const totalMB = (totalChars / 1024 / 1024).toFixed(2)

    if (performance.memory) {
      const heapMB = (performance.memory.usedJSHeapSize / 1024 / 1024).toFixed(2)
      console.log(`[FlexSearch] ✓ Indexed ${gists.length} gists (${totalMB} MB text) in ${buildTime}ms | Heap: ${heapMB} MB`)
    } else {
      console.log(`[FlexSearch] ✓ Indexed ${gists.length} gists (${totalMB} MB text) in ${buildTime}ms`)
    }

    setSearchIndex(index)
    setGistMap(map)
  }, [gists])

  const search = useCallback((query) => {
    if (!searchIndex || !query.trim()) {
      setSearchResults([])
      return []
    }

    try {
      const results = searchIndex.search(query, {
        limit: 100,
        suggest: true
      })

      const terms = query.trim().toLowerCase().split(/\s+/)
      const gistIds = new Set()
      const matchData = new Map()
      const deduped = []

      results.forEach(fieldResult => {
        if (fieldResult.result) {
          const field = fieldResult.field
          fieldResult.result.forEach(gist_id => {
            if (!matchData.has(gist_id)) {
              matchData.set(gist_id, {})
            }
            const match = matchData.get(gist_id)
            if (!match[field]) {
              match[field] = []
            }
            match[field].push(field)
          })
        }
      })

      results.forEach(fieldResult => {
        if (fieldResult.result) {
          fieldResult.result.forEach(gist_id => {
            if (!gistIds.has(gist_id)) {
              gistIds.add(gist_id)
              const gist = gistMap.get(gist_id)
              if (gist) {
                deduped.push({
                  gist_id: gist.gist_id,
                  description: gist.description,
                  terms: terms,
                  match: matchData.get(gist_id) || {}
                })
              }
            }
          })
        }
      })

      setSearchResults(deduped)
      return deduped
    } catch (error) {
      console.error('Search error:', error)
      setSearchResults([])
      return []
    }
  }, [searchIndex, gistMap])

  const clearSearch = useCallback(() => {
    setSearchResults([])
  }, [])

  return {
    searchIndex,
    searchResults,
    search,
    clearSearch,
  }
}
